import React, { useEffect, useRef, useState, useMemo } from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { useContentToken, EditableToken } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore — Vite ?url imports resolve to the served asset URL
import mapdataUrl from '../../assets/js/mapdata.min.js?url';
// @ts-ignore
import usmapUrl from '../../assets/js/usmap.js?url';
import { getPropertiesByState, getStateName } from './propertyUtils';
import '../styles/map-responsive.css';

export function MapAndProperties() {
  const propertiesByState = getPropertiesByState();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [expandedProps, setExpandedProps] = useState<Set<string>>(new Set());

  const stateGroups = useMemo(() => {
    return Object.values(propertiesByState)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(state => ({
        name: getStateName(state.abbr) || state.name,
        abbr: state.abbr,
        slug: state.slug,
        properties: Object.values(state.cities)
          .flatMap((city) =>
            (city.properties || []).map((p: any) => ({
              name: p.property_name,
              address: `${p.property_address || p.property_address_1 || ''}`,
              city: p.property_address_city,
              state: p.property_address_state,
              zip: p.property_address_zip,
              slug: p._slug || p.property_slug,
              stateSlug: p._stateSlug || state.slug,
              citySlug: p._citySlug || city.slug,
            }))
          )
          .sort((a: any, b: any) => a.name.localeCompare(b.name)),
      }));
  }, [propertiesByState]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const states: Record<string, any> = {};
    const allStateNames: Record<string, string> = {};
    for (const st of Object.values(propertiesByState)) { allStateNames[st.abbr] = st.name; }
    for (const abbr of Object.keys(allStateNames)) {
      states[abbr] = { name: allStateNames[abbr], inactive: 'yes' };
    }
    for (const state of Object.values(propertiesByState)) {
      const count = Object.values(state.cities)
        .reduce((sum: number, city) => sum + (city.properties?.length || 0), 0);
      states[state.abbr] = {
        name: state.name,
        color: '#ed1c24',
        description: `<a href="/apartments/${state.slug}/">${count} Location${count !== 1 ? 's' : ''}</a>`,
        hover_color: '#9e161b',
        count,
      };
    }

    (window as any).mapConfig = {
      states,
      colors: { active: '#ed1c24', hover: '#9e161b', default: '#c9c9c9', 'default-hover': '#9e161b', 'state-border': '#FFFFFF', 'default-pin': '#2041D4' },
      settings: { hide_labels: true, hide_eastern_labels: true, initial_zoom: -1 },
    };

    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => resolve();
        s.onerror = reject;
        document.body.appendChild(s);
      });

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if ((window as any).simplemaps_usmap) {
          (window as any).simplemaps_usmap.load();
        }
      }, 250);
    };

    loadScript(mapdataUrl)
      .then(() => loadScript(usmapUrl))
      .then(() => {
        if ((window as any).simplemaps_usmap) {
          (window as any).simplemaps_usmap.load();
        }
        window.addEventListener('resize', handleResize);
      })
      .catch(err => console.warn('[MapAndProperties] Failed to load simplemaps:', err));

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [propertiesByState]);

  const toggleProp = (key: string) => {
    setExpandedProps(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  if (!stateGroups.length) return null;

  return (
    <section className="case-map-section">
      <div className="case-container">
        <div className="case-map-grid">
          <div>
            <div className="case-property-list">
              <div className="case-property-list-holder">
                {stateGroups.map(sg => (
                  <div key={sg.abbr}>
                    <EditableToken path="neighborhoodmap.state_header_prefix" type="text" label="State Header Prefix" defaultValue={contentSchema.tokens['neighborhoodmap.state_header_prefix'].defaultValue} as="h2" className="case-property-state-header">
                      Properties in {sg.name}
                    </EditableToken>
                    <div>
                      {sg.properties.map((p: any) => {
                        const key = `${sg.abbr}-${p.slug}`;
                        const isOpen = expandedProps.has(key);
                        return (
                          <div key={key} className="case-property-item">
                            <button
                              className={isOpen ? 'expanded' : ''}
                              onClick={() => toggleProp(key)}
                            >
                              {p.name}
                              <svg width="16" height="10" viewBox="0 0 16 10" fill="currentColor">
                                <path d="M1.41.59 8 7.17 14.59.59 16 2 8 10 0 2z" />
                              </svg>
                            </button>
                            {isOpen && (
                              <div className="case-property-detail">
                                <p>{p.address}<br />{p.city}, {p.state} {p.zip}</p>
                                <a href={`/apartments/${p.stateSlug}/${p.citySlug}/${p.slug}/neighborhood`}>
                                  View Neighborhoods
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="case-map-col">
            <div id="usmap" ref={mapContainerRef} className="simple-map" />
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Map & Properties',
  tokens: {
    'neighborhoodmap.state_header_prefix': {
      label: 'State Header Prefix',
      type: 'text',
      description: 'Text prefix before state name in section headers',
      defaultValue: 'Properties in',
    },
  },
};

(MapAndProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default MapAndProperties;
